<template>
  <div id="app">
    <WebsocketHelper v-if="currentUser" />
    <b-navbar>
      <template #brand>
        <b-navbar-item tag="router-link" :to="{ path: '/' }">
          <img
            :src="
              fullyLoaded
                ? 'https://ask33.co.uk/assets/img/logo_short.png'
                : 'https://i.gifer.com/ZKZg.gif'
            "
            alt="Ask33 logo"
          />
        </b-navbar-item>
      </template>
      <template #start v-if="currentUser">
        <b-navbar-item>
          <router-link to="/search">Search</router-link>
        </b-navbar-item>
        <b-navbar-item>
          <router-link to="/applications">Your applications</router-link>
        </b-navbar-item>
        <b-navbar-item v-if="hasDrafts">
          <router-link to="/draft-students">Draft applications</router-link>
        </b-navbar-item>

        <b-navbar-dropdown label="Useful Info" hoverable>
          <b-navbar-item tag="router-link" :to="'/useful-info'">
            Useful Info
          </b-navbar-item>
          <b-navbar-item tag="router-link" :to="'/universities'">
            University list
          </b-navbar-item>
        </b-navbar-dropdown>

        <b-navbar-dropdown
          label="Moderator info"
          v-if="isModerator || isAdmin"
          hoverable
        >
          <b-navbar-item tag="router-link" :to="'/add-course'">
            Add new course
          </b-navbar-item>
          <b-navbar-item tag="router-link" :to="'/add-uni'">
            Add university
          </b-navbar-item>
        </b-navbar-dropdown>

        <b-navbar-dropdown label="Admin info" v-if="isAdmin" hoverable>
          <b-navbar-item tag="router-link" :to="'/users'">
            Users
          </b-navbar-item>
          <b-navbar-item tag="router-link" :to="'/intakes'">
            Intakes
          </b-navbar-item>
          <b-navbar-item tag="router-link" :to="'/register'">
            Create user accounts
          </b-navbar-item>
          <b-navbar-item tag="router-link" :to="'/excel-upload'">
            Excel upload
          </b-navbar-item>
          <!-- <b-navbar-item tag="router-link" :to="'/finance'">
            Finance
          </b-navbar-item> -->
        </b-navbar-dropdown>

        <b-navbar-dropdown
          label="Team leader info"
          v-if="currentUser.isTeamLeader || isManager"
          hoverable
        >
          <b-navbar-item>
            <router-link to="/view-team">View Team</router-link>
          </b-navbar-item>
        </b-navbar-dropdown>

        <b-navbar-item>
          <router-link to="/payment">Payment</router-link>
        </b-navbar-item>

        <b-navbar-item>
          <router-link to="/leaderboard">Leaderboard</router-link>
        </b-navbar-item>
      </template>

      <template #end>
        <b-navbar-item tag="div">
          <div class="buttons">
            <a class="button is-light login-navbar" v-if="!currentUser">
              <router-link to="/login"> Log in </router-link>
            </a>
          </div>
        </b-navbar-item>
        <b-navbar-item v-if="currentUser" @click="toggleNotifications">
          <b-icon
            :icon="activeNotifications.length ? 'bell-ring' : 'bell-off'"
            type="is-white"
          ></b-icon>
          <span v-if="activeNotifications.length" class="unread-messages">
            {{ activeNotifications.length }}
          </span>
        </b-navbar-item>
        <b-navbar-item v-if="currentUser">
          <router-link to="/messaging">
            <b-icon icon="facebook-messenger" type="is-white"></b-icon>
            <span v-if="showMessengerBubble" class="unread-messages">
              {{ unreadMessages }}
            </span>
          </router-link>
        </b-navbar-item>

        <b-navbar-item v-if="currentUser">
          <router-link to="/profile" class="profile-status">
            <div
              v-if="currentUser.status"
              class="navbar-status"
              :title="currentUser.status.text"
            >
              {{ currentUser.status.emoji }}
            </div>
            {{ currentUser.username }}
          </router-link>
        </b-navbar-item>
        <b-navbar-item v-if="currentUser">
          <a href @click.prevent="changeStatus(true)"> Status </a>
        </b-navbar-item>
      </template>
    </b-navbar>

    <change-status
      v-if="setStatusParent"
      :setStatus="setStatusParent"
      @closeChangeStatus="changeStatus(false)"
    ></change-status>

    <b-sidebar
      class="notification-sidebar"
      mobile
      type="is-light"
      can-cancel
      :open="showNotifications"
    >
      <NotificationsTab />
    </b-sidebar>

    <div
      v-if="(!isFilterLoading && currentUser) || !currentUser"
      class="container"
    >
      <router-view />
    </div>

    <b-loading v-else is-full-page :can-cancel="false"></b-loading>
  </div>
</template>

<script>
import userService from './services/user.service';
import UtilService from './services/util';
import ChangeStatus from './views/helpers/ChangeStatus.vue';
import WebsocketHelper from './views/helpers/WebsocketHelper.vue';
import NotificationsTab from './views/NotificationsTab.vue';

export default {
  data() {
    return {
      setStatusParent: false,
      sessionStartTime: null,
      dailyTimeSpent: 0,
      lastTrackingDate: null,
      showNotifications: false,
    };
  },
  components: {
    ChangeStatus,
    WebsocketHelper,
    NotificationsTab,
  },
  created() {
    window.addEventListener('beforeunload', this.endSession);
  },
  beforeUnmount() {
    this.endSession();
  },
  mounted() {
    this.verifySession();
  },
  computed: {
    isFilterLoading() {
      return this.$store.state.application_filter.isFilterLoading;
    },
    currentUser() {
      return this.$store.state.auth.user;
    },
    isManager() {
      return UtilService.isManager();
    },
    isModerator() {
      return UtilService.isModerator();
    },
    isAdmin() {
      return UtilService.isAdmin();
    },
    formattedTime() {
      const minutes = Math.floor(this.time / 60000);
      const seconds = ((this.time % 60000) / 1000).toFixed(0);
      return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    },
    unreadMessages() {
      return this.$store.state.message.unreadMessages;
    },
    activeNotifications() {
      return this.$store.state.application.notifications;
    },
    showMessengerBubble() {
      return this.$route.name !== 'messaging' && this.unreadMessages > 0;
    },
    hasDrafts() {
      return this.$store.state.student.maxId > 1;
    },
    fullyLoaded() {
      return (
        this.$store.state.application_filter.fullyLoaded ||
        !this.$store.state.auth.user
      );
    },
  },
  methods: {
    gtag() {
      if (window && window.gtag) {
        return window.gtag;
      }
      return function () {};
    },

    verifySession() {
      if (!this.currentUser) {
        return;
      }

      this.gtag()('set', {
        user_id: this.currentUser.username,
      });

      this.startSession();
      userService
        .verifyAuth()
        .then(() => {})
        .catch(() => {
          this.$buefy.dialog.alert({
            title: 'Session timeout',
            message: 'You have been logged on for quite a while. Please logout',
            type: 'is-danger',
            hasIcon: true,
            icon: 'times-circle',
            iconPack: 'fa',
            ariaRole: 'alertdialog',
            ariaModal: true,
            onConfirm: () => this.logOut(),
          });
        });
    },

    startSession() {
      this.sessionStartTime = new Date().getTime();
      // Add other session start logic here
    },

    endSession() {
      const sessionEndTime = new Date().getTime();
      const sessionDuration =
        (sessionEndTime - this.sessionStartTime) / 1000 / 60;
      const username = this.currentUser.username;
      this.updateDailyTimeSpent(sessionDuration);

      // Send the session duration to Google Analytics
      this.gtag('event', 'timing_complete', {
        name: 'User Session Duration',
        value: sessionDuration, // in minutes
        event_category: 'User',
        event_label: username,
      });

      this.sessionStartTime = null;
    },

    updateDailyTimeSpent(sessionDuration) {
      const currentDate = new Date().toISOString().split('T')[0];

      if (this.lastTrackingDate !== currentDate) {
        this.dailyTimeSpent = 0;
        this.lastTrackingDate = currentDate;
      }

      this.dailyTimeSpent += sessionDuration;

      this.gtag('event', 'daily_time_spent', {
        name: 'Daily Time Spent',
        value: this.dailyTimeSpent,
        event_category: 'User',
        event_label: this.currentUser.username,
      });
    },

    logOut() {
      this.endSession();
      this.$store.dispatch('auth/logout');
      this.$router.push('/login');
    },

    changeStatus(setStatus) {
      this.setStatusParent = setStatus;
    },

    toggleNotifications() {
      this.showNotifications = !this.showNotifications;
    },
  },
};
</script>

<style>
@import 'https://cdn.jsdelivr.net/npm/@mdi/font@5.8.55/css/materialdesignicons.min.css';
@import 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/4.7.0/css/font-awesome.min.css';
</style>
<style>
.toast {
  max-height: 44px;
}

.navbar-menu.is-active {
  background: #f9b040;
}

nav.navbar {
  background: #242021;
  margin-bottom: 20px;
}

nav.navbar .navbar-item {
  background: transparent;
  text-decoration: none;
}

nav.navbar .navbar-item:hover {
  color: #f9b040;
  background: transparent;
  text-decoration: none;
}

nav.navbar .navbar-item:hover a {
  color: #666666;
}

nav.navbar .navbar-item:hover.is-active .navbar-dropdown > a {
  color: #fff;
}

nav.navbar .navbar-item a {
  font-size: 15px;
  font-weight: 600;
  color: white;
  text-decoration: none;
}

nav.navbar .navbar-item a.router-link-active {
  color: #666666;
}

nav.navbar .navbar-item.has-dropdown {
  background: transparent;
}

nav.navbar .navbar-item.has-dropdown:hover .navbar-link,
nav.navbar .navbar-item.has-dropdown.is-active .navbar-link {
  color: #666;
  background: transparent;
}

nav.navbar .navbar-item.has-dropdown.is-active .navbar-dropdown {
  background: #666;
  border-top: none;
  top: calc(100% + 8px);
  padding-top: 0;
}

.navbar-dropdown a.navbar-item:focus,
.navbar-dropdown a.navbar-item:hover {
  color: #f9b040 !important;
}

nav.navbar .navbar-link {
  color: white;
  font-size: 15px;
  font-weight: 600;
}

nav.navbar .navbar-link:not(.is-arrowless)::after {
  border-color: #ffc451;
}

nav.navbar .navbar-link:hover {
  background: transparent;
  color: #ffc451;
  text-decoration: none;
}

nav.navbar a.navbar-item:focus-within {
  background-color: transparent;
}

button.button.is-primary[disabled] {
  border: none;
  background: #ffc451;
}

button.button,
a.button,
.dialog .modal-card .modal-card-foot button.button,
.dialog .modal-card .modal-card-foot button.button.is-primary {
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 600;
  display: inline-flex;
  justify-content: center;
  align-items: center;
}

button.button:hover,
a.button:hover,
.dialog .modal-card .modal-card-foot button.button:hover,
.dialog .modal-card .modal-card-foot button.button.is-primary:hover {
  text-decoration: none;
}

button.button.is-success,
a.button.is-success,
.dialog .modal-card .modal-card-foot button.button.is-success,
.dialog .modal-card .modal-card-foot button.button.is-primary.is-success,
button.button.is-primary,
a.button.is-primary,
.dialog .modal-card .modal-card-foot button.button.is-primary,
.dialog .modal-card .modal-card-foot button.button.is-primary.is-primary {
  background: #ffc451;
}

button.button.is-success:hover,
a.button.is-success:hover,
.dialog .modal-card .modal-card-foot button.button.is-success:hover,
.dialog .modal-card .modal-card-foot button.button.is-primary.is-success:hover,
button.button.is-primary:hover,
a.button.is-primary:hover,
.dialog .modal-card .modal-card-foot button.button.is-primary:hover,
.dialog .modal-card .modal-card-foot button.button.is-primary.is-primary:hover {
  background: #f7ac1c;
}

button.button.is-danger:hover,
a.button.is-danger:hover,
.dialog .modal-card .modal-card-foot button.button.is-danger:hover,
.dialog .modal-card .modal-card-foot button.button.is-primary.is-danger:hover {
  background: #f2133f;
}

p.is-green {
  color: #48c78e;
}

.b-table .table-wrapper.has-sticky-header {
  height: 80vh !important;
}

.container {
  max-width: 100% !important;
}

span.unread-messages {
  position: absolute;
  right: 0;
  border-radius: 50%;
  background: red;
  padding: 1px 6px;
  font-size: 10px;
  bottom: 10px;
}

.profile-status {
  display: flex;
}

.navbar-status {
  cursor: pointer;
}

nav.navbar .navbar-item .login-navbar a {
  color: rgba(0, 0, 0, 0.7);
}

.video-player.video-player-box {
  width: 100%;
}

.video-player .video-js {
  width: 100%;
}

.video-js button.vjs-big-play-button {
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

.tutorial-display {
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  flex-direction: column;
}

.tutorial-list {
  display: flex;
  flex-direction: column;
  min-width: 20vw;
  margin-right: 40px;
  box-shadow: rgba(50, 50, 93, 0.25) 0px 2px 5px -1px,
    rgba(0, 0, 0, 0.3) 0px 1px 3px -1px;
}

.tutorial-list > div {
  display: flex;
  flex-direction: column;
  padding-left: 28px;
  position: relative;
  cursor: pointer;
  min-height: 40px;
}

.tutorial-list > div.disabled {
  cursor: no-drop;
}

.tutorial-list > div.selected {
  background: rgb(227, 227, 227);
  cursor: pointer;
  position: relative;
}

.tutorial-list > div.selected:before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  width: 4px;
  height: 100%;
  background: #ffc451;
}

.tutorial-list > div span.icon {
  position: absolute;
  left: 3px;
  top: 14px;
}

.tutorial-list > div span.tutorial-title {
  font-size: 16px;
}

.tutorial-list > div.selected span.tutorial-title {
  font-weight: bold;
}

.tutorial-list > div.selected span.video-duration {
  display: block;
  font-size: 12px;
}

.tutorial-list > div span.video-duration {
  display: none;
}

.tutorial-display-text h5 {
  display: flex;
  justify-content: space-between;
  margin: 10px 0;
  font-size: 20px;
  font-weight: bolder;
  align-items: center;
}

.tutorial-display-text > span {
  font-size: 18px;
  font-weight: bold;
}

.tutorial-display-text > p {
  font-size: 16px;
  font-weight: 400;
  margin-top: 16px;
}

* {
  scrollbar-width: thin;
  scrollbar-color: #f9b040 #fff;
}

/* Chrome, Edge, and Safari */
*::-webkit-scrollbar {
  width: 5px;
  background: #fff;
}

*::-webkit-scrollbar-track {
  background: #fff;
}

*::-webkit-scrollbar-thumb {
  background-color: #f9b040;
  border-radius: 5px;
  border: 5px none #ffffff;
}

.cursor-pointer,
.icon.cursor-pointer {
  cursor: pointer;
}

.modal-close.is-large::before,
.modal-close.is-large::after {
  background-color: #363636;
}

.c-white {
  color: white !important;
}

.notification-sidebar.b-sidebar .sidebar-content {
  width: 630px;
}

.strike-through {
  text-decoration: line-through;
}
</style>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
